/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import * as HTMLEntities from "html-entities";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useSetRecoilState } from "recoil";
import * as CampaignApi from "../../../api/Campaign";
import {
  ActiveCampaignAtom,
  FilteredCampaignsAtom,
  TempCampaignAtom,
} from "../../../atoms/CampaignAtom";
import { parsePMFragmentToFragment } from "../../../hook/useEmailEditorPasting";
import { useModal } from "../../Modal/Modal";
import NoteEditor, { EditorHandle } from "./NoteEditor/NoteEditor";
import NoteModalFooter from "./NoteModalFooter";

interface NoteModalProps {
  campaignId: string;
  campaignNotes: string;
}
const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: inherit;

  width: 1000px;

  .date-wrapper {
    width: calc(100% + 50px);

    display: flex;
    align-items: center;
    justify-content: center;

    font-size: 14px;
    font-weight: 400;
    color: #979797;

    @media screen and (max-width: 1024px) {
      width: 100%;

      margin-top: 0;

      border-top: none;
    }
  }

  @media screen and (max-width: 1024px) {
    width: 100vw;
    height: 100%;
  }
`;

interface NoteModalProps {
  campaignId: string;
  campaignName: string;
  campaignNotes: string;
  campaignLastNotesUpdate: Date;
}

const NoteModal = ({
  campaignId,
  campaignName,
  campaignNotes,
  campaignLastNotesUpdate,
}: NoteModalProps) => {
  const [notes, setNotes] = useState(campaignNotes);
  const setTempCampaign = useSetRecoilState(TempCampaignAtom);
  const setActiveCampaign = useSetRecoilState(ActiveCampaignAtom);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasContent, setHasContent] = useState<boolean>(false);
  const editorHandlerRef = useRef<EditorHandle>(null);
  const setFilteredCampaign = useSetRecoilState(FilteredCampaignsAtom);
  const modal = useModal();

  const onSave = async () => {
    if (!campaignId) return;
    try {
      setIsLoading(true);
      const lastNotesUpdate = new Date();
      const response = await CampaignApi.updateCampaign(campaignId, {
        notes,
        lastNotesUpdate,
      });
      setFilteredCampaign((campaigns) =>
        campaigns.map((c) => (c._id.toString() === campaignId ? response : c))
      );
      setTempCampaign((c) => (c ? response : c));
      setActiveCampaign((c) => (c ? response : c));
      toast("Notes successfully saved!", {
        type: "success",
      });
      modal.popModal();
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onCancel = () => {
    modal.popModal();
  };

  const onCopyText = async () => {
    const editor = editorHandlerRef.current?.getEditor();
    let html = editor.getHTML();
    html = HTMLEntities.decode(html);

    html = html.replace(/<style([\s\S]*?)<\/style>/gi, "");
    html = html.replace(/<script([\s\S]*?)<\/script>/gi, "");
    html = html.replace(/<\/div>/gi, "\n");
    html = html.replace(/<\/li>/gi, "\n");
    html = html.replace(/<li>/gi, "  *  ");
    html = html.replace(/<\/ul>/gi, "\n");
    html = html.replace(/<\/p>/gi, "\n");
    html = html.replace(/<br\s*[\/]?>/gi, "\n");
    html = html.replace(/<[^>]+>/gi, "");

    await navigator.clipboard.writeText(html);

    toast("Content successfully copied to the clipboard!", {
      type: "success",
    });
  };

  const onCopyTaskItems = () => {
    const editor = editorHandlerRef.current?.getEditor();
    const tasks = [];
    editor.state.doc.descendants((node, post, parent) => {
      if (node.type.name === "taskItem") {
        const htmlContent = parsePMFragmentToFragment(
          node.content,
          editor.state.schema
        );

        let html = htmlContent.firstElementChild.innerHTML;
        html = HTMLEntities.decode(html);

        html = html.replace(/<style([\s\S]*?)<\/style>/gi, "");
        html = html.replace(/<script([\s\S]*?)<\/script>/gi, "");
        html = html.replace(/<\/div>/gi, "\n");
        html = html.replace(/<\/li>/gi, "\n");
        html = html.replace(/<li>/gi, "  *  ");
        html = html.replace(/<\/ul>/gi, "\n");
        html = html.replace(/<\/p>/gi, "\n");
        html = html.replace(/<br\s*[\/]?>/gi, "\n");
        html = html.replace(/<[^>]+>/gi, "");
        html = `"${html}"`;

        tasks.push(html);
      }
    });

    const csv = tasks.join(",");
    const hiddenElement = document.createElement("a");
    hiddenElement.href = "data:text/csv;charset=utf-8," + encodeURI(csv);
    hiddenElement.target = "_blank";
    hiddenElement.download = `${campaignName} Notes.csv`;
    hiddenElement.click();

    toast("Content successfully parsed!", {
      type: "success",
    });
  };

  useEffect(() => {
    const editor = editorHandlerRef.current?.getEditor();
    let html = editor?.getText();

    if (!!html) {
      setHasContent(true);
      return;
    } else {
      setHasContent(false);
      return;
    }
  }, [notes, modal]);

  return (
    <ModalBody>
      <NoteEditor
        ref={editorHandlerRef}
        notes={notes}
        setNotes={setNotes}
        campaignLastNotesUpdate={campaignLastNotesUpdate}
      />
      <NoteModalFooter
        isLoading={isLoading}
        onSave={onSave}
        onCancel={onCancel}
        onCopyText={onCopyText}
        onCopyTaskItems={onCopyTaskItems}
        hasNotesContent={hasContent}
      />
    </ModalBody>
  );
};

export default NoteModal;
